/**
 * Contract Actions Hook
 * US-CONT-005: View Expiring Contracts Alert
 * Handles contract quick actions (renew, download, contact, etc.)
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useRenewContract, useDownloadContract } from './useExpiringContracts';
import type {
  ContractAction,
  ContractActionType,
  ExpiringContract,
  RenewalRequest
} from '../types/expiringContracts.types';
import { ACTION_LABELS } from '../constants/expiringContracts.constants';

/**
 * Hook to handle contract actions
 * @returns Action handler functions
 */
export function useContractActions() {
  const navigate = useNavigate();
  const renewContractMutation = useRenewContract();
  const downloadContractMutation = useDownloadContract();

  /**
   * Handle contract action
   * @param action - Contract action object
   */
  const handleAction = useCallback(
    (action: ContractAction) => {
      switch (action.type) {
        case 'RENEW':
          handleRenew(action.contract, action.metadata);
          break;
        case 'VIEW_DETAILS':
          handleViewDetails(action.contract);
          break;
        case 'CONTACT_CUSTOMER':
          handleContactCustomer(action.contract);
          break;
        case 'DOWNLOAD':
          handleDownload(action.contract);
          break;
        case 'EDIT':
          handleEdit(action.contract);
          break;
        case 'TERMINATE':
          handleTerminate(action.contract);
          break;
        default:
          console.warn('Unknown action type:', action.type);
      }
    },
    [navigate, renewContractMutation, downloadContractMutation]
  );

  /**
   * Handle renew contract action
   * Opens renewal modal or navigates to renewal page
   */
  const handleRenew = useCallback(
    (contract: ExpiringContract, metadata?: Record<string, unknown>) => {
      if (metadata && 'renewalRequest' in metadata) {
        // If renewal request is provided, execute directly
        const request = metadata.renewalRequest as RenewalRequest;
        renewContractMutation.mutate(request);
      } else {
        // Navigate to renewal page
        navigate(`/contracts/${contract.id}/renew`);
      }
    },
    [navigate, renewContractMutation]
  );

  /**
   * Handle view details action
   * Navigates to contract details page
   */
  const handleViewDetails = useCallback(
    (contract: ExpiringContract) => {
      navigate(`/contracts/${contract.id}`);
    },
    [navigate]
  );

  /**
   * Handle contact customer action
   * Opens email client or phone dialer based on available contact info
   */
  const handleContactCustomer = useCallback((contract: ExpiringContract) => {
    const { customerEmail, customerPhone, customerName } = contract;

    if (customerEmail && customerPhone) {
      // Show options dialog
      toast.info('Contact Options', {
        description: `Email: ${customerEmail} | Phone: ${customerPhone}`,
        action: {
          label: 'Send Email',
          onClick: () => {
            window.location.href = `mailto:${customerEmail}?subject=Contract Renewal - ${contract.contractNumber}`;
          }
        }
      });
    } else if (customerEmail) {
      // Open email client
      window.location.href = `mailto:${customerEmail}?subject=Contract Renewal - ${contract.contractNumber}&body=Dear ${customerName},%0D%0A%0D%0AYour contract ${contract.contractNumber} is expiring soon.`;
    } else if (customerPhone) {
      // Open phone dialer
      window.location.href = `tel:${customerPhone}`;
    } else {
      toast.error('No contact information available for this customer');
    }
  }, []);

  /**
   * Handle download contract action
   * Downloads contract PDF
   */
  const handleDownload = useCallback(
    (contract: ExpiringContract) => {
      downloadContractMutation.mutate(contract.id);
    },
    [downloadContractMutation]
  );

  /**
   * Handle edit contract action
   * Navigates to contract edit page
   */
  const handleEdit = useCallback(
    (contract: ExpiringContract) => {
      navigate(`/contracts/${contract.id}/edit`);
    },
    [navigate]
  );

  /**
   * Handle terminate contract action
   * Opens termination dialog (handled by parent component)
   * This is a placeholder that delegates to parent handler
   */
  const handleTerminate = useCallback((contract: ExpiringContract) => {
    // This action is handled by the parent component via TerminateContractDialog
    // The parent should pass a custom handler via metadata
    console.log('Terminate action triggered for contract:', contract.contractNumber);
  }, []);

  /**
   * Create action object
   * Helper to create properly typed action objects
   */
  const createAction = useCallback(
    (type: ContractActionType, contract: ExpiringContract, metadata?: Record<string, unknown>): ContractAction => {
      return {
        type,
        contract,
        metadata
      };
    },
    []
  );

  /**
   * Get action label
   * Gets display label for an action type
   */
  const getActionLabel = useCallback((type: ContractActionType): string => {
    return ACTION_LABELS[type] || type;
  }, []);

  /**
   * Check if action is available for contract
   * Determines if an action can be performed on a contract
   */
  const isActionAvailable = useCallback((type: ContractActionType, contract: ExpiringContract): boolean => {
    switch (type) {
      case 'RENEW':
        // Can only renew active or expiring contracts
        return contract.status === 'ACTIVE' || contract.status === 'EXPIRING_SOON';
      case 'TERMINATE':
        // Can only terminate active contracts
        return contract.status === 'ACTIVE' || contract.status === 'EXPIRING_SOON';
      case 'EDIT':
        // Can't edit expired or terminated contracts
        return contract.status !== 'EXPIRED' && contract.status !== 'TERMINATED';
      default:
        // Other actions are always available
        return true;
    }
  }, []);

  /**
   * Get loading state for action
   */
  const isActionLoading = useCallback(
    (type: ContractActionType): boolean => {
      switch (type) {
        case 'RENEW':
          return renewContractMutation.isPending;
        case 'DOWNLOAD':
          return downloadContractMutation.isPending;
        default:
          return false;
      }
    },
    [renewContractMutation.isPending, downloadContractMutation.isPending]
  );

  return {
    // Main action handler
    handleAction,

    // Individual action handlers
    handleRenew,
    handleViewDetails,
    handleContactCustomer,
    handleDownload,
    handleEdit,
    handleTerminate,

    // Helper functions
    createAction,
    getActionLabel,
    isActionAvailable,
    isActionLoading,

    // Mutation states
    isRenewing: renewContractMutation.isPending,
    isDownloading: downloadContractMutation.isPending,
    renewError: renewContractMutation.error,
    downloadError: downloadContractMutation.error
  };
}

/**
 * Hook to get available actions for a contract
 * @param contract - Contract object
 * @returns List of available action types
 */
export function useAvailableActions(contract: ExpiringContract): ContractActionType[] {
  const { isActionAvailable } = useContractActions();

  return useCallback(() => {
    const allActions: ContractActionType[] = [
      'VIEW_DETAILS',
      'RENEW',
      'CONTACT_CUSTOMER',
      'DOWNLOAD',
      'EDIT',
      'TERMINATE'
    ];

    return allActions.filter(action => isActionAvailable(action, contract));
  }, [contract, isActionAvailable])();
}
